import React, { useEffect, useState, useRef } from 'react';
import './teamLeaves.css';
import { useTranslation } from 'react-i18next';
import axiosInstance from '../../../utils/axiosInstance';
import { Spinner, Alert } from 'react-bootstrap';

const getColor = (index) => {
    const colors = ['#f7cfcf', '#b3e2ff', '#cdffb3'];
    return colors[index % colors.length];
};

const getInitials = (name) => {
    return name.split(' ').map(part => part[0]).join('');
};

const formatLeaveDates = (startDate, endDate) => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    if (start.getTime() === end.getTime()) {
        return `${start.getDate()} ${start.toLocaleString('default', { month: 'short' })}`;
    }
    return `${start.getDate()} - ${end.getDate()} ${start.toLocaleString('default', { month: 'short' })}`;
};

export const TeamLeaves = ({ adminIds, empRole }) => {
    const { t } = useTranslation();
    const [todayLeaves, setTodayLeaves] = useState([]);
    const [monthLeaves, setMonthLeaves] = useState([]);
    const [loading, setLoading] = useState(true); // Loading state
    const todayLeavesRef = useRef(null);  // Ref for today leaves scrolling
    const scrollAmount = 60;  // Scroll by the width of one circle (adjust as needed)

    useEffect(() => {
        const fetchLeaves = async () => {
            setLoading(true);
            try {
                const response = await axiosInstance.get('/admin/leaves');
                const data = response.data;

                let filteredData;
                if (empRole === 'SuperAdmin') {
                    filteredData = data;
                } else {
                    filteredData = data.filter(leave => adminIds.includes(Number(leave.id)));
                }

                const todayLeaves = filteredData.filter(leave => {
                    const start = new Date(leave.startDate);
                    const end = new Date(leave.endDate);
                    const today = new Date();
                    start.setHours(0, 0, 0, 0);
                    end.setHours(0, 0, 0, 0);
                    today.setHours(0, 0, 0, 0);
                    return start <= today && end >= today;
                });

                const monthLeavesMap = new Map();
                const currentMonth = new Date().getMonth();
                const currentYear = new Date().getFullYear();

                filteredData.forEach(leave => {
                    const start = new Date(leave.startDate);
                    const end = new Date(leave.endDate);
                    if ((start.getMonth() === currentMonth && start.getFullYear() === currentYear) ||
                        (end.getMonth() === currentMonth && end.getFullYear() === currentYear)) {

                        const formattedDates = formatLeaveDates(leave.startDate, leave.endDate);
                        const employeeName = leave.name;

                        if (monthLeavesMap.has(employeeName)) {
                            monthLeavesMap.get(employeeName).push(formattedDates);
                        } else {
                            monthLeavesMap.set(employeeName, [formattedDates]);
                        }
                    }
                });

                const monthLeaves = Array.from(monthLeavesMap).map(([name, dates], index) => ({
                    name,
                    dates: dates.join(', '),
                }));

                setTodayLeaves(todayLeaves);
                setMonthLeaves(monthLeaves);

            } catch (error) {
                console.error('Error fetching leaves:', error.message);
            } finally {
                setLoading(false);
            }
        };

        fetchLeaves();
    }, [adminIds]);

    // Function to scroll the "today-leaves" horizontally
    const handleScroll = (direction) => {
        if (todayLeavesRef.current) {
            const scrollValue = direction === 'left' ? -scrollAmount : scrollAmount;
            todayLeavesRef.current.scrollBy({ left: scrollValue, behavior: 'smooth' });
        }
    };

    return (
        <div className="team-leaves">
            <p className="titleHead">{t("dashboard_todayTeamLEave")}</p>
            {todayLeaves.length > 0 && (
                <>
                    <button className="carousel-button prev" onClick={() => handleScroll('left')}></button>
                    <button className="carousel-button next" onClick={() => handleScroll('right')}></button>
                </>
            )}
            <div className="today-leaves" ref={todayLeavesRef}>
                {loading ? (
                    <div className="team-leaves-Spinner">
                        <Spinner animation="border" role="status">
                            <span className=""></span>
                        </Spinner><br />
                        <span className="mt-2">{t('Please wait your data is loading')}</span>
                    </div>
                ) : todayLeaves.length > 0 ? (
                    todayLeaves.map((employee, index) => (
                        <div
                            key={index}
                            className="circle"
                            style={{ borderColor: getColor(index) }}
                            title={employee.name}
                        >
                            {getInitials(employee.name)}
                        </div>
                    ))
                ) : (
                    <Alert variant="light" className='team-leaves-Alert'>
                        {t("dashboard_noLeaveToday")}
                    </Alert>
                    // <p className="no-activities">{t("dashboard_noLeaveToday")}</p>
                )}
            </div>
            <p className="titleHead">{t("dashboard_monthsTeamLEave")}</p>
            <div className="month-leaves">
                {loading ? (
                    <div className="team-leaves-Spinner">
                        <Spinner animation="border" role="status">
                            <span className=""></span>
                        </Spinner><br />
                        <span className="mt-2">{t('Please wait your data is loading')}</span>
                    </div>
                ) : monthLeaves.length > 0 ? (
                    monthLeaves.map((employee, index) => (
                        <div key={index} className="employee-leaves">
                            <div className="circle" title={employee.name} style={{ borderColor: getColor(index) }}>
                                {getInitials(employee.name)}
                            </div>
                            <div className="employee-info">
                                <div className="employee-details">
                                    <div className="employee-names">{employee.name}</div>
                                </div>
                                <div className="leave-dates">{employee.dates}</div>
                            </div>
                        </div>
                    ))
                ) : (
                    <Alert variant="light" className='team-leaves-Alert'>
                        {t("dashboard_noLeaveMonth")}
                    </Alert>
                    // <p className="no-activities">{t("dashboard_noLeaveMonth")}</p>
                )}
            </div>
        </div>
    );
};

export default TeamLeaves;
